import type { PickRoute, PickTote, RoutePutBack } from '@wms/domain';
import { useState, type ReactNode } from 'react';
import { api, errorDetails, errorMessage } from '../api';
import { customerText, fmt } from '../format';
import { navigate } from '../router';
import { useSession } from '../session';
import type { OrderSummary, PickRouteSummary } from '../types';
import { ChannelBadge, Empty, ErrorBanner, Modal, SellerChip, Spinner, useLoad, useToast } from '../ui';
import { Icon } from '../ui/icons';
import { useSort } from '../ui/sort';

const MAX_TOTES = 12;

/** A route's short name on the floor and on the packing desk: "Rota 3F9A". */
export const routeCode = (id: string) => id.slice(-4).toUpperCase();

/** "Rota 3F9A · Sepet 4"; the cart of a single-order route has no numbered totes. */
export const toteText = (tote: PickTote) => `Rota ${routeCode(tote.routeId)}${tote.toteSlot !== null ? ` · Sepet ${tote.toteSlot}` : ''}`;

/** What a cancelled route had taken for orders sent back to the queue, bin by bin. */
export function PutBackList({ items }: { items: readonly RoutePutBack[] }) {
  return (
    <div className="table-wrap">
      <table className="t">
        <tbody>
          {items.map((p, i) => (
            <tr key={i}>
              <td className="mono">
                <b>{p.locationCode}</b>
              </td>
              <td>
                {p.name}
                <div className="muted" style={{ fontSize: 12.5 }}>
                  <span className="mono">
                    {p.sku} · {p.orderNumber}
                  </span>
                  {/* the list of a route cancelled before it kept the marketplaces has none */}
                  {p.channel && (
                    <>
                      {' '}
                      <ChannelBadge channel={p.channel} short />
                    </>
                  )}
                </div>
              </td>
              <td className="num">
                <b>{p.qty}</b> adet
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/**
 * "Rotayı iptal et", also mid-route (e.g. to add a newly arrived order to a fresh route): fully
 * picked orders go on to packing, the others back to "Toplamaya hazır"; what was already taken for
 * those is listed bin by bin, to put back.
 */
export function CancelRouteDialog(props: { routeId: string; picked: number; onClose: () => void; onCancelled: () => void; labelled?: number }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [putBack, setPutBack] = useState<readonly RoutePutBack[] | null>(null);

  const cancel = async () => {
    setBusy(true);
    setError(null);
    try {
      const result = await api<{ putBack: RoutePutBack[] }>('POST', `/api/wms/pick-routes/${props.routeId}/cancel`);
      if (result.putBack.length === 0) props.onCancelled();
      else setPutBack(result.putBack);
    } catch (e) {
      setError(errorMessage(e));
      setBusy(false);
    }
  };

  return (
    <Modal
      title={putBack ? 'Rota iptal edildi' : 'Rotayı iptal et'}
      // the list closes only with its button: a stray tap must not lose what goes back where
      onClose={putBack ? () => undefined : props.onClose}
      footer={
        putBack ? (
          <button className="btn primary" onClick={props.onCancelled}>
            Ürünleri geri koydum
          </button>
        ) : (
          <>
            <button className="btn" onClick={props.onClose}>
              Vazgeç
            </button>
            <button className="btn danger" disabled={busy} onClick={() => void cancel()}>
              Rotayı iptal et
            </button>
          </>
        )
      }
    >
      <div className="stack">
        {error && <div className="banner bad">{error}</div>}
        {(props.labelled ?? 0) > 0 && (
          <div className="banner warn">
            Bu rota için {props.labelled} kargo etiketi basıldı. Yapıştırdıklarınızı ürünlerden sökün, hepsini atın: siparişler yeniden toplanınca yenisi
            basılır.
          </div>
        )}
        {putBack ? (
          <>
            <p style={{ margin: 0 }}>
              Yarım kalan siparişler toplamaya hazır listesine döndü. Onlar için alınan ürünleri gözlerine geri koyun (sepeti toplayan siz değilseniz,
              toplayıcının ekranında da bu liste görünür):
            </p>
            <PutBackList items={putBack} />
          </>
        ) : (
          <p style={{ margin: 0 }}>
            {props.picked === 0
              ? 'Rotadaki siparişler toplamaya hazır listesine döner; yeni siparişlerle birlikte yeniden rota oluşturabilirsiniz.'
              : 'Tamamı toplanmış siparişler paketlemeye gider. Yarım kalanlar toplamaya hazır listesine döner; onlar için alınan ürünleri gözlerine geri koymanız için liste gösterilir.'}
          </p>
        )}
      </div>
    </Modal>
  );
}

/** Open routes to continue, the orders ready to be released onto a new route, and today's finished routes. */
export function PickRoutes() {
  const { warehouseId } = useSession();
  const toast = useToast();
  const query = warehouseId ? `warehouseId=${warehouseId}` : '';
  const routes = useLoad(() => api<PickRouteSummary[]>('GET', `/api/wms/pick-routes?${query}`), [query], 15_000);
  const ready = useLoad(() => api<OrderSummary[]>('GET', `/api/orders?status=allocated&sort=oldest&limit=200&${query}`), [query], 15_000);
  const finished = useLoad(() => api<PickRouteSummary[]>('GET', `/api/wms/pick-routes?status=completed,cancelled&today=true&limit=50&${query}`), [query], 30_000);
  const [selected, setSelected] = useState<ReadonlySet<string>>(new Set());
  const [busy, setBusy] = useState(false);
  const [problems, setProblems] = useState<{ message: string; details: string[] } | null>(null);
  const [cancelling, setCancelling] = useState<PickRouteSummary | null>(null);

  const toggle = (id: string) => {
    if (!selected.has(id) && selected.size >= MAX_TOTES) {
      toast(`Bir arabada en fazla ${MAX_TOTES} sipariş toplanır`, 'bad');
      return;
    }
    setSelected((current) => {
      const next = new Set(current);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const create = async (orderIds: readonly string[]) => {
    if (!warehouseId || orderIds.length === 0) return;
    setBusy(true);
    setProblems(null);
    try {
      const route = await api<PickRoute>('POST', '/api/wms/pick-routes', {
        warehouseId,
        orderIds,
        mode: orderIds.length === 1 ? 'single' : 'batch',
      });
      navigate(`/toplama/${route.id}`);
    } catch (e) {
      setProblems({ message: errorMessage(e), details: errorDetails(e) });
      void ready.reload();
    } finally {
      setBusy(false);
    }
  };

  const orders = ready.data ?? [];
  const oldest = orders.slice(0, 8).map((o) => o.id);
  const sort = useSort<OrderSummary>('pick-queue', {
    order: { label: 'Sipariş', value: (o) => o.orderNumber },
    seller: { label: 'Satıcı', value: (o) => o.tenantCode },
    customer: { label: 'Müşteri', value: (o) => o.customerName },
    units: { label: 'Adet', value: (o) => o.units, desc: true },
    // the longest waiting first
    waiting: { label: 'Bekleme', value: (o) => o.orderedAt },
  });

  return (
    <>
      <section className="card">
        <div className="card-h">
          <h2>Devam eden rotalar</h2>
          <span className="sub">Bir rotaya dokunup kaldığınız duraktan devam edin</span>
        </div>
        {routes.error && <ErrorBanner message={routes.error} onRetry={() => void routes.reload()} />}
        {!routes.data ? (
          <Spinner />
        ) : routes.data.length === 0 ? (
          <Empty title="Açık rota yok">Aşağıdan sipariş seçip yeni bir rota oluşturun.</Empty>
        ) : (
          <div className="route-list">
            {routes.data.map((r) => (
              <RouteItem
                key={r.id}
                route={r}
                chip={<span className={`chip ${r.status === 'in_progress' ? 'info' : ''}`}>{r.status === 'in_progress' ? 'Toplanıyor' : 'Bekliyor'}</span>}
                detail={`${r.pendingStops} / ${r.stopCount} durak kaldı`}
                when={`${fmt.age(r.createdAt)} önce açıldı`}
                action={
                  <button className="btn ghost route-cancel" onClick={() => setCancelling(r)}>
                    İptal
                  </button>
                }
              />
            ))}
          </div>
        )}
      </section>
      {cancelling && (
        <CancelRouteDialog
          routeId={cancelling.id}
          picked={cancelling.stopCount - cancelling.pendingStops}
          onClose={() => setCancelling(null)}
          onCancelled={() => {
            setCancelling(null);
            toast('Rota iptal edildi', 'ok');
            void routes.reload();
            void ready.reload();
            void finished.reload();
          }}
        />
      )}

      <section className="card pick-ready">
        <div className="card-h">
          <h2>Toplamaya hazır siparişler</h2>
          <span className="sub">
            {sort.sorted ? '' : 'En eski sipariş en üstte · '}
            {orders.length} sipariş
          </span>
          <span className="spacer" />
          {sort.picker}
          <button className="btn" disabled={busy || orders.length === 0} onClick={() => setSelected(new Set(oldest))}>
            En eski {Math.min(8, orders.length)} siparişi seç
          </button>
          <button className="btn primary" disabled={busy || selected.size === 0} onClick={() => void create([...selected])}>
            <Icon name="route" />
            {selected.size <= 1 ? 'Tekli rota oluştur' : `Toplu rota oluştur (${selected.size} sepet)`}
          </button>
        </div>
        {problems && (
          <div className="card-b">
            <div className="banner bad">
              <div>
                {problems.message}
                {problems.details.length > 0 && (
                  <ul style={{ margin: '6px 0 0', paddingLeft: 18, fontWeight: 500 }}>
                    {problems.details.map((d) => (
                      <li key={d}>{d}</li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
          </div>
        )}
        {ready.error && <ErrorBanner message={ready.error} onRetry={() => void ready.reload()} />}
        {!ready.data ? (
          <Spinner />
        ) : orders.length === 0 ? (
          <Empty title="Toplanacak sipariş yok">Rezerve edilen siparişler burada belirir.</Empty>
        ) : (
          <div className="table-wrap">
            <table className="t stack-sm">
              <thead>
                <tr>
                  <th />
                  {sort.th('order')}
                  {sort.th('seller')}
                  {sort.th('customer', 'hide-sm')}
                  {sort.th('units', 'num')}
                  {sort.th('waiting')}
                </tr>
              </thead>
              <tbody>
                {sort.apply(orders).map((o) => (
                  <tr key={o.id} className={`clickable${selected.has(o.id) ? ' selected' : ''}`} onClick={() => toggle(o.id)}>
                    <td>
                      <input type="checkbox" checked={selected.has(o.id)} onChange={() => toggle(o.id)} onClick={(e) => e.stopPropagation()} aria-label={`${o.orderNumber} seç`} />
                    </td>
                    <td>
                      <span className="mono">{o.orderNumber}</span> <ChannelBadge channel={o.channel} />
                    </td>
                    <td>
                      <SellerChip code={o.tenantCode} />
                    </td>
                    <td className="hide-sm">{customerText(o)}</td>
                    <td className="num" data-label="Adet">{o.units}</td>
                    <td>{fmt.age(o.orderedAt)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <section className="card">
        <div className="card-h">
          <h2>Bugün tamamlanan rotalar</h2>
          <span className="sub">Dokunun: rotanın son ekranı açılır (sepetler, geri al)</span>
        </div>
        {finished.error && <ErrorBanner message={finished.error} onRetry={() => void finished.reload()} />}
        {!finished.data ? (
          <Spinner />
        ) : finished.data.length === 0 ? (
          <Empty title="Bugün biten rota yok" />
        ) : (
          <div className="route-list">
            {finished.data.map((r) => (
              <RouteItem
                key={r.id}
                route={r}
                chip={
                  <>
                    <span className={`chip ${r.status === 'completed' ? 'ok' : ''}`}>{r.status === 'completed' ? 'Tamamlandı' : 'İptal edildi'}</span>
                    {r.leftOrders > 0 && <span className="chip bad">{r.leftOrders} sipariş rotadan çıktı</span>}
                  </>
                }
                detail={`${r.stopCount} durak`}
                when={r.completedAt ? `${fmt.age(r.completedAt)} önce bitti` : ''}
              />
            ))}
          </div>
        )}
      </section>
    </>
  );
}

/** A route as one tappable card: readable and easy to hit on a phone. */
function RouteItem(props: { route: PickRouteSummary; chip: ReactNode; detail: string; when: string; action?: ReactNode }) {
  const { route: r } = props;
  return (
    <div className="route-item">
      <a className="route-main" href={`#/toplama/${r.id}`}>
        <span className="route-title">
          <b className="mono">Rota {routeCode(r.id)}</b>
          {props.chip}
          {/* where its orders came from: "TY ×3", "HB" */}
          {r.channels.map((c) => (
            <ChannelBadge key={c.channel} channel={c.channel} short count={c.orders} />
          ))}
        </span>
        <span className="route-sub">
          {r.mode === 'batch' ? 'Toplu (sepetli)' : 'Tekli'} · {r.orderCount} sipariş · {props.detail}
          {r.totalDistanceM !== null && ` · ${fmt.dec(r.totalDistanceM)} m`}
        </span>
        {props.when && <span className="route-when">{props.when}</span>}
      </a>
      {props.action}
    </div>
  );
}
